import { Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import _ from 'lodash'
import { of } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'
import { CouponCodeTargetType, CouponCodeValueType } from '../../../../pages/coupon-codes/models/coupon-code'
import { AutomationType } from '../../../../pages/sms/modules/sms-automations/models/automation'
import { StoreState } from '../../../../store/store.state'
import { SMSService } from '../../../services/sms.service'
import { CampaignCard, CampaignCardValues } from '../models/campaign-cards.model'
import { UserShopType } from '../../../models/user/user-shop-type.model'
import { ApiCampaignService } from '../../../../core/services/api/api-campaign.service'
import { CampaignService } from '../../../../core/services/campaign.service'
import { GettingStartedService } from '../../../../core/services/getting-started.service'
import { UserService } from '../../../../core/services/user.service'
import { SetGettingStartedCampaign } from '../../../../store/getting-started/getting-started.actions'
import { CampaignPluginName, Campaign, CampaignState } from '../../../models/campaign/campaign'
import { GettingStartedGroupName } from '../../../models/getting-started/getting-started-group-name.model'
import { RouteHeaderUrl } from '../../../components/one-header/header-navigation.model'
import { TutorialsService } from '../../../../pages/tutorials/services/tutorials.service'

@Injectable()
export class CampaignCardsHelperService {

  smsAutomationForCard = {
    [CampaignCardValues.SMSUpsell]: AutomationType.upsell,
    [CampaignCardValues.SMSWinback]: AutomationType.winback,
    [CampaignCardValues.SMSBirthday]: AutomationType.birthday,
    [CampaignCardValues.SMSCartRecovery]: AutomationType.cart,
  }

  discountTypeForCard = {
    [CampaignCardValues.FixedAmount]: CouponCodeValueType.fixed_amount,
    [CampaignCardValues.Percentage]: CouponCodeValueType.percentage,
    [CampaignCardValues.FreeShipping]: CouponCodeTargetType.shipping_line,
    [CampaignCardValues.BuyXGetY]: CouponCodeValueType.buy_x_get_y,
  }

  pluginForCard = {
    [CampaignCardValues.FortuneWheel]: CampaignPluginName.FortuneWheel,
    [CampaignCardValues.RecentSalesPop]: CampaignPluginName.RecentActivity,
    [CampaignCardValues.LiveVisitorCount]: CampaignPluginName.LiveVisitorCount,
    [CampaignCardValues.AggregateSalesPop]: CampaignPluginName.AggregateActivity,
    [CampaignCardValues.AggregateVisitorCount]: CampaignPluginName.AggregateVisitorCount,
    [CampaignCardValues.NewsLetterNotification]: CampaignPluginName.NewsLetterNotification,
    [CampaignCardValues.CouponBoxNotification]: CampaignPluginName.CouponBoxNotification,
    [CampaignCardValues.FreeShippingNotification]: CampaignPluginName.FreeShipping,
    [CampaignCardValues.SalesNotification]: CampaignPluginName.SalesPopup,
    [CampaignCardValues.ProductPopup]: CampaignPluginName.ProductPopup,
    [CampaignCardValues.CollectPhoneNumbers]: CampaignPluginName.NewsLetterNotification,
    [CampaignCardValues.CampaignEmails]: CampaignPluginName.NewsLetterNotification,
  }

  videoTutorialAvailable = this.userService?.userInfo.country_code !== 'CN'

  constructor(
    private userService: UserService,
    private smsService: SMSService,
    private gettingStartedService: GettingStartedService,
    private campaignsService: CampaignService,
    private apiCampaignService: ApiCampaignService,
    private tutorialService: TutorialsService,
    private store: Store<StoreState>,
  ) {
  }

  // If shopTypes key not exist, then it will show on both non-shopify and shopify sections for non-shopify users
  // If shopTypes key exist, but is it empty array, then it will show on both non-shopify and shopify sections for shopify users
  // UserShopType.CustomShop is for non-shopify users only
  get campaignCards(): CampaignCard[] {
    return [
      {
        img: '/app_assets/apps/upsell.svg',
        badges: ['Upsell'],
        title: 'Post-Purchase Upsell & Cross Sell',
        titleKey: 'apps_page.card_titles.upsell_card',
        description: 'Far more than a sales technique, upsell marketing is a crucial part of a customer engagement strategy  that prioritizes customer satisfaction and retention.',
        descriptionKey: 'apps_page.modal.upsell_card_parent_desc',
        value: CampaignCardValues.UpsellCard,
        setup_mins: 5,
        keywords: ['increase customers'],
        shopTypes: [UserShopType.ShopifyShop],
        children_open: false,
        hasTutorial: this.videoTutorialAvailable,
        videoTutorial: {
          tutorialType: 'library',
          currentVideo: this.tutorialService.getVideoById('WZYrIeaZpcY'),
          segmentEvent: 'Upsell'
        },
        children: [
          {
            inToolbox: true,
            toolboxOpen: true,
            toolboxSectionName: 'upsellCheckout',
            toolboxTitle: 'After checkout is completed, immediately show:',
            toolboxTitleKey: 'apps_page.modal.tb_after_checkout_immediate',
            toolboxChildren: [
              {
                badges: ['Automation'],
                title: 'Upsell Page (One-Click Checkout)',
                titleKey: 'apps_page.modal.upsell_page_one_click_title',
                description: 'Customers can accept upsell offers with one-click (no need to re-enter payment information).',
                descriptionKey: 'apps_page.modal.upsell_page_one_click_desc',
                value: CampaignCardValues.UpsellCheckout,
                img: '/app_assets/apps/post-checkout-upsell.svg'
              },
            ],
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            inToolbox: true,
            toolboxOpen: true,
            toolboxSectionName: 'upsellMessage',
            toolboxTitle: 'After checkout is completed, schedule:',
            toolboxTitleKey: 'apps_page.modal.tb_after_checkout_schedule',
            toolboxChildren: [
              {
                badges: ['Automation'],
                title: 'Upsell Message (Email)',
                titleKey: 'apps_page.modal.upsell_msg_email_title',
                description: 'Recommend products and offer discounts based on recent purchases.',
                descriptionKey: 'apps_page.modal.upsell_msg_recommend_desc',
                value: CampaignCardValues.UpsellEmails,
                img: '/app_assets/themes/emails/presets/upsell.svg'
              },
              {
                badges: ['Automations'],
                title: 'Upsell Message (SMS)',
                titleKey: 'apps_page.modal.upsell_msg_sms_title',
                description: 'Recommend products and offer discounts based on recent purchases.',
                descriptionKey: 'apps_page.modal.upsell_msg_recommend_desc',
                value: CampaignCardValues.SMSUpsell,
                tcpa_required: true,
                img: '/app_assets/themes/sms/presets/upsell.svg'
              },
            ],
            shopTypes: [UserShopType.ShopifyShop],
          },
        ]
      },
      {
        img: '/app_assets/apps/chat-gpt.svg',
        title: 'ChatGPT-like AI Assistant (Sidekick)',
        titleKey: 'apps_page.card_titles.chat_gpt',
        description: '',
        value: CampaignCardValues.ChatGPT,
        openUrl: 'one-gpt',
        setup_mins: 1,
        keywords: ['increase customers'],
        shopTypes: [],
        children_open: false,
        children: [],
      },
      {
        img: '/app_assets/apps/cart.svg',
        badges: ['Cart Recovery'],
        title: 'Checkout Abandonment Protector',
        titleKey: 'apps_page.card_titles.recovery_card',
        description: 'Send automated Emails and SMS to Reduce Cart Abandonment.',
        descriptionKey: 'apps_page.modal.recovery_desc_parent',
        value: CampaignCardValues.RecoveryCard,
        setup_mins: 5,
        keywords: ['increase customers'],
        shopTypes: [UserShopType.ShopifyShop],
        children_open: false,
        children: [
          {
            badges: ['Automation'],
            title: 'Email Automation | Cart Recovery',
            titleKey: 'apps_page.modal.recovery_email_cart_title',
            description: 'Reduce cart abandonment and drive sales with a reminder email.',
            descriptionKey: 'apps_page.modal.recovery_email_cart_desc',
            value: CampaignCardValues.CartRecoveryEmails,
            img: '/app_assets/themes/emails/presets/cart.svg',
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            badges: ['Automations'],
            title: 'SMS Automation | Cart Recovery',
            titleKey: 'apps_page.modal.recovery_sms_cart_title',
            description: 'Recover abandon carts more effectively with 98% text message open rates.',
            descriptionKey: 'apps_page.modal.recovery_sms_cart_desc',
            value: CampaignCardValues.SMSCartRecovery,
            tcpa_required: true,
            img: '/app_assets/themes/sms/presets/cart.svg',
            shopTypes: [UserShopType.ShopifyShop],
          },
        ]
      },
      {
        img: '/app_assets/apps/optin-popups.svg',
        badges: ['Capture Leads', 'Exit Intent', 'Gift Box Teaser'],
        title: 'Opt-in Pop ups',
        titleKey: 'apps_page.card_titles.capture_leads',
        description: 'Skyrocket SMS, Email, Birthday lists with 60+ beautifully designed pop up themes, customizable triggers, CSS, JS, teasers, and more.',
        descriptionKey: 'apps_page.modal.capture_leads_desc',
        value: CampaignCardValues.CaptureLeads,
        setup_mins: 5,
        keywords: ['grow email list', 'increase customers'],
        shopTypes: [],
        children_open: false,
        hide_title: true,
        hasTutorial: this.videoTutorialAvailable,
        videoTutorial: {
          tutorialType: 'library',
          currentVideo: this.tutorialService.getVideoById('IvJ4nSIHrYA'),
          segmentEvent: 'Popups'
        },
        children: [
          {
            title: 'Gamified Pop ups',
            titleKey: 'apps_page.modal.sep_gamified_popups',
            type: 'separator',
            hasTutorial: this.videoTutorialAvailable,
            videoTutorial: {
              tutorialType: 'library',
              currentVideo: this.tutorialService.getVideoById('IvJ4nSIHrYA'),
              segmentEvent: 'Popups'
            },
            shopTypes: [],
          },
          {
            badges: [{text: '9.3% Opt-In Rate', color: 'purple'}, {text: 'Recommended', color: 'success'}],
            title: 'Spin Wheel',
            titleKey: 'apps_page.modal.spin_wheel_title',
            description: 'Offer a chance to win prizes and discounts.',
            descriptionKey: 'apps_page.modal.spin_wheel_desc',
            value: CampaignCardValues.FortuneWheel,
            quickstart: true,
            badges_visible: true,
            img: '/app_assets/themes/plugins/presets/wheel.svg',
            shopTypes: [],
          },
          {
            title: 'Traditional Pop ups',
            titleKey: 'apps_page.modal.sep_traditional_popups',
            type: 'separator',
            shopTypes: [],
          },
          {
            badges: [{text: '5.2% Opt-In Rate', color: 'purple'}, {text: 'Recommended', color: 'success'}],
            title: 'Pop up (With Reward)',
            titleKey: 'apps_page.modal.popup_with_reward_title',
            description: 'Incentives help capture leads and drive higher conversion rates.',
            descriptionKey: 'apps_page.modal.popup_with_reward_desc',
            value: CampaignCardValues.CouponBoxNotification,
            quickstart: true,
            badges_visible: true,
            img: '/app_assets/themes/plugins/presets/bcb.svg',
            shopTypes: [],
          },
          {
            badges: [{text: '3.5% Opt-In Rate', color: 'purple'}],
            title: 'Pop up (No Reward)',
            titleKey: 'apps_page.modal.popup_no_reward_title',
            description: 'Subscribe to newsletters of product updates, news, and VIP promotions.',
            descriptionKey: 'apps_page.modal.popup_no_reward_desc',
            value: CampaignCardValues.NewsLetterNotification,
            quickstart: true,
            badges_visible: true,
            img: '/app_assets/themes/plugins/presets/bsb.svg',
            shopTypes: [],
          },
        ]
      },
      {
        img: '/app_assets/apps/announcement-pop-ups.svg',
        badges: ['Free Shipping', 'Count Down'],
        title: 'Announcement Pop ups',
        titleKey: 'apps_page.card_titles.announcement',
        description: 'Announcement notifications',
        descriptionKey: 'apps_page.modal.announcement_desc',
        value: CampaignCardValues.Announcement,
        setup_mins: 5,
        keywords: [],
        shopTypes: [],
        children_open: false,
        hide_title: true,
        hasTutorial: this.videoTutorialAvailable,
        videoTutorial: {
          tutorialType: 'library',
          currentVideo: this.tutorialService.getVideoById('cp2SB-FkaOs'),
          segmentEvent: 'Announcement Popup'
        },
        children: [
          {
            title: 'Announcement Pop ups',
            titleKey: 'apps_page.modal.sep_announcement_popups',
            type: 'separator',
            hasTutorial: this.videoTutorialAvailable,
            videoTutorial: {
              tutorialType: 'library',
              currentVideo: this.tutorialService.getVideoById('cp2SB-FkaOs'),
              segmentEvent: 'Announcement Popup'
            },
          },
          {
            badges: [{text: 'Recommended', color: 'success'}],
            title: 'Free Shipping',
            titleKey: 'apps_page.modal.announcement_free_shipping_title',
            description: 'Motivate customers to shop by offering free shipping.',
            descriptionKey: 'apps_page.modal.announcement_free_shipping_desc',
            value: CampaignCardValues.FreeShippingNotification,
            badges_visible: false,
            img: '/app_assets/themes/plugins/presets/free-shipping-pop-up.svg',
            shopTypes: [],
          },
          {
            badges: [{text: 'Recommended', color: 'success'}],
            title: 'Sales Announcement',
            titleKey: 'apps_page.modal.announcement_sales_title',
            description: 'Notify shoppers about flash sales and upcoming sales',
            descriptionKey: 'apps_page.modal.announcement_sales_desc',
            value: CampaignCardValues.SalesNotification,
            badges_visible: false,
            img: '/app_assets/themes/plugins/presets/sales-popup.svg',
            shopTypes: [],
          },
          {
            badges: [{text: 'Recommended', color: 'success'}],
            title: 'Product Announcement',
            titleKey: 'apps_page.modal.announcement_product_title',
            description: 'Showcase new product drops, restocks, and pre-orders',
            descriptionKey: 'apps_page.modal.announcement_product_desc',
            value: CampaignCardValues.ProductPopup,
            badges_visible: false,
            img: '/app_assets/apps/recommendations.svg',
            shopTypes: [UserShopType.ShopifyShop],
          },
        ]
      },
      {
        img: '/app_assets/apps/email-marketing.svg',
        badges: ['Cart Recovery', 'Birthday', 'Recommendations'],
        title: 'Email Marketing',
        titleKey: 'apps_page.card_titles.email_automation',
        description: 'Welcome series, birthdays, cart abandonment, reward reminders, product recommendations, autoresponders and more!',
        descriptionKey: 'apps_page.modal.email_marketing_desc',
        value: CampaignCardValues.EmailAutomation,
        setup_mins: 5,
        keywords: ['marketing', 'announcements', 'automation', 'broadcast'],
        shopTypes: [],
        children_open: false,
        hide_title: true,
        hasTutorial: this.videoTutorialAvailable,
        videoTutorial: {
          tutorialType: 'modal',
          currentVideo: this.tutorialService.getVideoById('RMdXZeiOynQ'),
          segmentEvent: 'Email Overview'
        },
        children: [
          // {
          //   title: 'Email Onboarding',
          //   type: 'separator',
          //   hasTutorial: this.videoTutorialAvailable,
          //   videoTutorial: {
          //     tutorialType: 'modal',
          //     currentVideo: this.tutorialService.getVideoById('RMdXZeiOynQ'),
          //     segmentEvent: 'Email Overview'
          //   }
          // },
          // {
          //   badges: [],
          //   title: 'Email Onboarding',
          //   description: 'Initial setup, branding, compliance, and quotas.',
          //   value: CampaignCardValues.EmailOnboarding,
          //   img: '/app_assets/email-onboarding/email-onboarding.svg',
          //   button_text: 'Start',
          // },
          {
            title: 'Email Broadcasts',
            titleKey: 'apps_page.modal.sep_email_broadcasts',
            type: 'separator',
            shopTypes: [],
          },
          {
            inToolbox: true,
            toolboxOpen: true,
            toolboxSectionName: 'broadcasts',
            toolboxTitle: 'One-time email blast, schedule:',
            toolboxTitleKey: 'apps_page.modal.tb_email_blast_once',
            toolboxChildren: [
              {
                badges: ['Automation'],
                title: 'Newsletter Email (With Reward)',
                titleKey: 'apps_page.modal.email_newsletter_reward_title',
                description: 'Schedule bulk emails with rewards to subscribers.',
                descriptionKey: 'apps_page.modal.email_newsletter_reward_desc',
                value: CampaignCardValues.BroadcastCoupon,
                img: '/app_assets/themes/emails/presets/discount.svg',
              },
              {
                badges: ['Automation'],
                title: 'Newsletter Email (No Reward)',
                titleKey: 'apps_page.modal.email_newsletter_no_reward_title',
                description: 'Schedule bulk emails to subscribers.',
                descriptionKey: 'apps_page.modal.email_newsletter_no_reward_desc',
                value: CampaignCardValues.BroadcastNewsletter,
                img: '/app_assets/themes/emails/presets/newsletter.svg',
              },
            ],
            shopTypes: [],
          },
          {
            title: 'Email Automations',
            titleKey: 'apps_page.modal.sep_email_automations',
            type: 'separator',
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            inToolbox: true,
            toolboxOpen: true,
            toolboxSectionName: 'abandonment',
            toolboxTitle: 'When customer abandons cart or checkout, send:',
            toolboxTitleKey: 'apps_page.modal.tb_when_abandon',
            toolboxChildren: [
              {
                badges: ['Automation'],
                title: 'Cart Recovery Email',
                titleKey: 'apps_page.modal.email_cart_recovery_title',
                description: 'Reduce cart abandonment and drive sales with a reminder email.',
                descriptionKey: 'apps_page.modal.email_cart_recovery_desc',
                value: CampaignCardValues.CartRecoveryEmails,
                img: '/app_assets/themes/emails/presets/cart.svg',
              },
            ],
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            inToolbox: true,
            toolboxOpen: true,
            toolboxSectionName: 'upsell',
            toolboxTitle: 'When customer makes a purchase, send:',
            toolboxTitleKey: 'apps_page.modal.tb_when_purchase',
            toolboxChildren: [
              {
                badges: ['Automation'],
                title: 'Upsell Email',
                titleKey: 'apps_page.modal.email_upsell_title',
                description: 'Recommend a product to the customer based on what they purchased.',
                descriptionKey: 'apps_page.modal.email_upsell_desc',
                value: CampaignCardValues.UpsellEmails,
                img: '/app_assets/themes/emails/presets/upsell.svg',
              },
              {
                badges: ['Automation'],
                title: 'Thank You Email (1st Purchase)',
                titleKey: 'apps_page.modal.email_thank_you_first_title',
                description: 'Thank first-time customers with a reward to encourage more purchases and brand loyalty.',
                descriptionKey: 'apps_page.modal.email_thank_you_first_desc',
                value: CampaignCardValues.WelcomeNewCustomerCard,
                img: '/app_assets/themes/emails/presets/welcome_customers.svg',
              },
            ],
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            inToolbox: true,
            toolboxOpen: true,
            toolboxSectionName: 'loyalty',
            toolboxTitle: 'When customer hasn’t bought recently, send:',
            toolboxTitleKey: 'apps_page.modal.tb_when_inactive',
            toolboxChildren: [
              {
                badges: ['Automation'],
                title: 'Birthday Email',
                titleKey: 'apps_page.modal.email_birthday_title',
                description: 'Build customer loyalty with Happy Birthday gift or discount offer.',
                descriptionKey: 'apps_page.modal.email_birthday_desc',
                value: CampaignCardValues.BirthdayEmails,
                img: '/app_assets/themes/emails/presets/birthday.svg',
              },
              {
                badges: ['Automation'],
                title: 'Winback Email',
                titleKey: 'apps_page.modal.email_winback_title',
                description: 'Target customers who have not purchased anything in the specified period.',
                descriptionKey: 'apps_page.modal.email_winback_desc',
                value: CampaignCardValues.WinbackEmails,
                img: '/app_assets/themes/emails/presets/winback.svg',
              },
            ],
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            inToolbox: true,
            toolboxOpen: true,
            toolboxSectionName: 'welcome',
            toolboxTitle: 'When new customer signs up, send:',
            toolboxTitleKey: 'apps_page.modal.tb_when_new_signup',
            toolboxChildren: [
              {
                badges: ['Automation'],
                title: 'Thank You Email (Shopify Subscribers)',
                titleKey: 'apps_page.modal.email_thank_you_shopify_subs_title',
                description: 'Thank new customers who subscribed through Shopify footer section, checkout, password, or coming soon pages.',
                descriptionKey: 'apps_page.modal.email_thank_you_shopify_subs_desc',
                value: CampaignCardValues.ThankYou,
                img: '/app_assets/themes/emails/presets/welcome_customers.svg',
              },
              {
                badges: ['Automation'],
                title: 'Welcome Email (All Subscribers)',
                titleKey: 'apps_page.modal.email_welcome_all_subs_title',
                description: 'Welcome and introduce your brand to new customers who subscribed through either ONE+ or Shopify.',
                descriptionKey: 'apps_page.modal.email_welcome_all_subs_desc',
                value: CampaignCardValues.WelcomeNewSubscriberCard,
                img: '/app_assets/themes/emails/presets/welcome_sub.svg',
              },
              {
                badges: ['Automation'],
                title: 'Welcome Email (Shopify Accounts)',
                titleKey: 'apps_page.modal.email_welcome_accounts_title',
                description: 'Welcome newly created accounts and encourage conversions.',
                descriptionKey: 'apps_page.modal.email_welcome_accounts_desc',
                value: CampaignCardValues.WelcomeNewAccountsCard,
                img: '/app_assets/themes/emails/presets/welcome_accounts.svg',
              },
            ],
            shopTypes: [UserShopType.ShopifyShop],
          },
          // {
          //   badges: ['Automation'],
          //   title: 'Email Automation | Welcome',
          //   description: 'Send thank you emails & reward redemption reminders.',
          //   value: CampaignCardValues.CampaignEmails,
          //   img: '/app_assets/themes/emails/presets/welcome_sub.svg'
          // },
        ]
      },
      {
        img: '/app_assets/apps/sms-marketing.svg',
        badges: ['Cart Recovery', 'Upsell', 'Recommendations'],
        title: 'SMS Marketing',
        titleKey: 'apps_page.card_titles.sms_automation',
        description: 'Has open rates as high as 98%, compared to just 20% for emails! A must have marketing channel at least for your North American customers.',
        descriptionKey: 'apps_page.modal.sms_marketing_desc',
        value: CampaignCardValues.SMSAutomation,
        setup_mins: 12,
        keywords: [
          'sales', 'sms', 'automation', 'tcpa', 'upsell', 'text', 'subscriptions', 'marketing', 'sms marketing'
        ],
        shopTypes: [UserShopType.ShopifyShop],
        children_open: false,
        hide_title: true,
        children: [
          {
            title: 'SMS Onboarding',
            titleKey: 'apps_page.modal.sep_sms_onboarding',
            type: 'separator',
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            inToolbox: true,
            toolboxOpen: true,
            toolboxSectionName: 'smsOnboardingStart',
            toolboxTitle: 'Required SMS onboarding, start:',
            toolboxTitleKey: 'apps_page.modal.tb_sms_onboarding_required',
            toolboxChildren: [
              {
                badges: ['TCPA', 'Keywords'],
                title: 'SMS Onboarding',
                titleKey: 'apps_page.modal.sms_onboarding_title',
                description: 'TCPA & GDPR compliance, dedicated SMS phone number, and keywords.',
                descriptionKey: 'apps_page.modal.sms_onboarding_desc',
                value: CampaignCardValues.TCPACompliance,
                button_text: 'Start',
                button_text_key: 'apps_page.modal.start',
                img: '/app_assets/apps/sms-onboarding.svg',
              },
              {
                badges: ['Pop Ups'],
                title: 'Collect Numbers',
                titleKey: 'apps_page.modal.collect_numbers_title',
                description: 'Checkout, pop ups, and/or text keyword.',
                descriptionKey: 'apps_page.modal.collect_numbers_desc',
                value: CampaignCardValues.CollectPhoneNumbers,
                tcpa_required: true,
                img: '/app_assets/apps/collect-numbers.svg',
              },
            ],
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            title: 'SMS Broadcasts',
            titleKey: 'apps_page.modal.sep_sms_broadcasts',
            type: 'separator',
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            inToolbox: true,
            toolboxOpen: true,
            toolboxSectionName: 'broadcasts',
            toolboxTitle: 'One-time SMS blast, schedule:',
            toolboxTitleKey: 'apps_page.modal.tb_sms_blast_once',
            toolboxChildren: [
              {
                badges: ['Broadcasts'],
                title: 'Newsletter SMS (With Reward)',
                titleKey: 'apps_page.modal.sms_newsletter_coupon_title',
                description: this.isShopifyShop ? 'Offer discount or gift card to incentivize customers and leads to buy!' : this.isOtherShop ? 'Offer discount to incentivize customers and leads to buy!' : 'Offer discount or download link to incentivize customers and leads to take action!',
                descriptionKey: this.isShopifyShop
                  ? 'apps_page.modal.sms_newsletter_coupon_desc_shopify'
                  : this.isOtherShop
                    ? 'apps_page.modal.sms_newsletter_coupon_desc_other'
                    : 'apps_page.modal.sms_newsletter_coupon_desc_generic',
                value: CampaignCardValues.SMSBroadcastCoupon,
                tcpa_required: true,
                img: '/app_assets/presets/sms_broadcast_coupon.svg'
              },
              {
                badges: ['Broadcasts'],
                title: 'Newsletter SMS (No Reward)',
                titleKey: 'apps_page.modal.sms_newsletter_no_coupon_title',
                description: 'Give subscribers product and other business updates!',
                descriptionKey: 'apps_page.modal.sms_newsletter_no_coupon_desc',
                value: CampaignCardValues.SMSBroadcastNoCoupon,
                tcpa_required: true,
                img: '/app_assets/presets/sms_broadcast_newsletter.svg'
              },
            ],
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            title: 'SMS Automations',
            titleKey: 'apps_page.modal.sep_sms_automations',
            type: 'separator',
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            inToolbox: true,
            toolboxOpen: true,
            toolboxSectionName: 'abandonment',
            toolboxTitle: 'When customer abandons cart or checkout, send:',
            toolboxTitleKey: 'apps_page.modal.tb_when_abandon',
            toolboxChildren: [
              {
                badges: ['Automations'],
                title: 'Cart Recovery SMS',
                titleKey: 'apps_page.modal.sms_cart_recovery_title',
                description: 'Reduce cart abandonment with 98% text message open rates.',
                descriptionKey: 'apps_page.modal.sms_cart_recovery_desc',
                value: CampaignCardValues.SMSCartRecovery,
                tcpa_required: true,
                img: '/app_assets/themes/sms/presets/cart.svg'
              },
            ],
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            inToolbox: true,
            toolboxOpen: true,
            toolboxSectionName: 'upsell',
            toolboxTitle: 'When customer makes a purchase, send:',
            toolboxTitleKey: 'apps_page.modal.tb_when_purchase',
            toolboxChildren: [
              {
                badges: ['Automations'],
                title: 'Upsell SMS',
                titleKey: 'apps_page.modal.sms_upsell_title',
                description: 'Recommend a product to the customer based on what they purchased.',
                descriptionKey: 'apps_page.modal.sms_upsell_desc',
                value: CampaignCardValues.SMSUpsell,
                tcpa_required: true,
                img: '/app_assets/themes/sms/presets/upsell.svg'
              },
            ],
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            inToolbox: true,
            toolboxOpen: true,
            toolboxSectionName: 'loyalty',
            toolboxTitle: 'When customer hasn’t bought recently, send:',
            toolboxTitleKey: 'apps_page.modal.tb_when_inactive',
            toolboxChildren: [
              {
                badges: ['Automations'],
                title: 'Birthday SMS',
                titleKey: 'apps_page.modal.sms_birthday_title',
                description: 'Build customer loyalty with Happy Birthday gift or discount offer.',
                descriptionKey: 'apps_page.modal.sms_birthday_desc',
                value: CampaignCardValues.SMSBirthday,
                tcpa_required: true,
                img: '/app_assets/themes/sms/presets/birthday.svg'
              },
              {
                badges: ['Automations'],
                title: 'WinBack SMS',
                titleKey: 'apps_page.modal.sms_winback_title',
                description: 'Win-back customers who have not purchased anything in the specified period',
                descriptionKey: 'apps_page.modal.sms_winback_desc',
                value: CampaignCardValues.SMSWinback,
                tcpa_required: true,
                img: '/app_assets/themes/sms/presets/winback.svg'
              },
            ],
            shopTypes: [UserShopType.ShopifyShop],
          },
        ]
      },
      {
        img: '/app_assets/apps/social-proof.svg',
        badges: ['Recent Sales Notifications', 'Live Visitor Count'],
        title: 'Social Proof',
        titleKey: 'apps_page.card_titles.social_proof',
        description: 'Notifications are a genuine way to capture visitor attention, establish trust, and create urgency to buy!',
        descriptionKey: 'apps_page.modal.social_proof_desc',
        value: CampaignCardValues.SocialProof,
        setup_mins: 1,
        keywords: ['marketing', 'socialproof'],
        shopTypes: [],
        children_open: false,
        hasTutorial: this.videoTutorialAvailable,
        videoTutorial: {
          tutorialType: 'modal',
          currentVideo: this.tutorialService.getVideoById('tHP04-CWBXM'),
          segmentEvent: 'Social Proof'
        },
        children: [
          {
            badges: ['Social Proof'],
            title: 'Sales Pop',
            titleKey: 'apps_page.modal.social_sales_pop_title',
            description: 'Show off recent sales history.',
            descriptionKey: 'apps_page.modal.social_sales_pop_desc',
            value: CampaignCardValues.RecentSalesPop,
            quickstart: true,
            setup_mins: 4,
            img: '/app_assets/apps/sales-pop.svg',
            shopTypes: [],
          },
          {
            badges: ['Social Proof'],
            title: 'Aggregate Sales Pop',
            titleKey: 'apps_page.modal.social_agg_sales_pop_title',
            description: 'Show off sales over a time period.',
            descriptionKey: 'apps_page.modal.social_agg_sales_pop_desc',
            value: CampaignCardValues.AggregateSalesPop,
            quickstart: true,
            setup_mins: 4,
            locked_text: 'Upgrade to Use',
            locked_text_key: 'apps_page.modal.upgrade_to_use',
            img: '/app_assets/apps/aggregate-sales-pop.svg',
            shopTypes: [],
          },
          {
            badges: ['Social Proof'],
            title: 'Visitor Counter',
            titleKey: 'apps_page.modal.social_visitor_count_title',
            description: 'Real-time count of visitors on your shop.',
            descriptionKey: 'apps_page.modal.social_visitor_count_desc',
            value: CampaignCardValues.LiveVisitorCount,
            quickstart: true,
            setup_mins: 4,
            img: '/app_assets/apps/visitor-counter.svg',
            shopTypes: [],
          },
          {
            badges: ['Social Proof'],
            title: 'Aggregate Visitor Counter',
            titleKey: 'apps_page.modal.social_agg_visitor_title',
            description: 'Show off visitors over a time period.',
            descriptionKey: 'apps_page.modal.social_agg_visitor_desc',
            value: CampaignCardValues.AggregateVisitorCount,
            quickstart: true,
            setup_mins: 4,
            locked_text: 'Upgrade to Use',
            locked_text_key: 'apps_page.modal.upgrade_to_use',
            img: '/app_assets/apps/aggregate-visitor-counter.svg',
            shopTypes: [],
          },
        ],
      },
      {
        img: '/app_assets/apps/spin-wheel.svg',
        badges: ['Gamification'],
        title: 'Gamification',
        titleKey: 'apps_page.card_titles.gamification',
        description: 'Don’t annoy, but excite customers to opt-in and redeem offers. Over 35,000 ONE+ brands we see an average of 9.3% opt-in rate!',
        descriptionKey: 'apps_page.modal.gamification_desc',
        value: CampaignCardValues.Gamification,
        setup_mins: 5,
        keywords: ['gamification', 'marketing', 'pop ups'],
        shopTypes: [],
        children_open: false,
        hasTutorial: this.videoTutorialAvailable,
        videoTutorial: {
          tutorialType: 'modal',
          currentVideo: this.tutorialService.getVideoById('IvJ4nSIHrYA'),
          segmentEvent: 'Gamification'
        },
        children: [
          {
            badges: [{text: '9.3% Opt-In Rate', color: 'purple'}, {text: 'Recommended', color: 'success'}],
            title: 'Spin Wheel',
            titleKey: 'apps_page.modal.spin_wheel_title',
            description: 'Offer a chance to win prizes and discounts.',
            descriptionKey: 'apps_page.modal.spin_wheel_desc',
            quickstart: true,
            value: CampaignCardValues.FortuneWheel,
            badges_visible: true,
            img: '/app_assets/themes/plugins/presets/wheel.svg',
            shopTypes: [],
          },
        ]
      },
      {
        img: '/app_assets/apps/thank_you.svg',
        badges: ['Thank You'],
        title: 'Thank You',
        titleKey: 'apps_page.card_titles.thank_you',
        description: 'Thank you messages are a great way to get your customers to return to your shop.',
        descriptionKey: 'apps_page.modal.thank_you_card_desc',
        value: CampaignCardValues.ThankYou,
        setup_mins: 5,
        keywords: ['Thank You'],
        shopTypes: [UserShopType.ShopifyShop],
        children_open: false,
        children: [
          {
            badges: ['Automation'],
            title: 'Thank You | 1st Purchase',
            titleKey: 'apps_page.modal.thank_you_first_purchase_title',
            description: 'Thank first-time customers with a reward to encourage more purchases and brand loyalty.',
            descriptionKey: 'apps_page.modal.thank_you_first_purchase_desc',
            value: CampaignCardValues.WelcomeNewCustomerCard,
            img: '/app_assets/themes/emails/presets/welcome_customers.svg',
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            badges: ['Automation'],
            title: 'Thank You Email (Shopify Subscribers)',
            titleKey: 'apps_page.modal.thank_you_shopify_subscribers_title',
            description: 'Thank new customers who subscribed through Shopify footer section, checkout, password, or coming soon pages.',
            descriptionKey: 'apps_page.modal.thank_you_shopify_subscribers_desc',
            value: CampaignCardValues.ThankYou,
            img: '/app_assets/themes/emails/presets/welcome_sub.svg',
            shopTypes: [UserShopType.ShopifyShop],
          },
        ]
      },
      {
        img: '/app_assets/apps/welcome.svg',
        badges: ['Welcome'],
        title: 'Welcome',
        titleKey: 'apps_page.card_titles.welcome',
        description: 'Give your new subscribers, customers, and accounts a warm welcome when they sign up.',
        descriptionKey: 'apps_page.modal.welcome_card_desc',
        value: CampaignCardValues.Welcome,
        setup_mins: 5,
        keywords: ['welcome'],
        shopTypes: [],
        children_open: false,
        children: [
          {
            badges: ['Automation'],
            title: 'Welcome | New Subscribers',
            titleKey: 'apps_page.modal.welcome_new_subscribers_title',
            description: 'Welcome new subscribers with an introduction to your brand and encourage conversions.',
            descriptionKey: 'apps_page.modal.welcome_new_subscribers_desc',
            value: CampaignCardValues.WelcomeNewSubscriberCard,
            img: '/app_assets/themes/emails/presets/welcome_sub.svg',
            shopTypes: [],
          },
          {
            badges: ['Automation'],
            title: 'Welcome | New Accounts',
            titleKey: 'apps_page.modal.welcome_new_accounts_title',
            description: 'Welcome newly created accounts and encourage conversions.',
            descriptionKey: 'apps_page.modal.welcome_new_accounts_desc',
            value: CampaignCardValues.WelcomeNewAccountsCard,
            img: '/app_assets/themes/emails/presets/welcome_accounts.svg',
            shopTypes: [UserShopType.ShopifyShop],
          },
        ]
      },
      {
        img: '/app_assets/apps/discounts.svg',
        badges: ['Buy X Get Y', 'Unique Codes', 'Buy One Get One'],
        title: 'Discount Codes',
        titleKey: 'apps_page.card_titles.discount_codes',
        description: 'Generate, manage, and measure analytics of personalized & unique reward codes.',
        descriptionKey: 'coupon_form.discount_codes_card_description',
        value: CampaignCardValues.Discounts,
        setup_mins: 1,
        keywords: ['sales', 'increase customers', 'coupons', 'rewards'],
        shopTypes: [],
        children_open: false,
        children: [
          {
            badges: ['Discount'],
            title: 'Buy X get Y',
            titleKey: 'coupon_form.opt_buy_x_get_y',
            description: 'Rewards with free gift for buying X.',
            descriptionKey: 'coupon_form.discount_card_desc_buy_x_get_y',
            value: CampaignCardValues.BuyXGetY,
            img: '/app_assets/apps/buy-x-get-y.svg',
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            badges: ['Discount'],
            title: 'Free Shipping',
            titleKey: 'coupon_form.opt_free_shipping',
            description: 'Rewards with free shipping.',
            descriptionKey: 'coupon_form.discount_card_desc_free_shipping',
            value: CampaignCardValues.FreeShipping,
            img: '/app_assets/apps/free-shipping.svg',
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            badges: ['Discount'],
            title: 'Percentage',
            titleKey: 'coupon_form.opt_percentage',
            description: 'Rewards with percentage off.',
            descriptionKey: 'coupon_form.discount_card_desc_percentage',
            value: CampaignCardValues.Percentage,
            img: '/app_assets/apps/percentage-off.svg',
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            badges: ['Discount'],
            title: 'Fixed Amount',
            titleKey: 'coupon_form.opt_fixed_amount',
            description: 'Rewards with fixed amount off.',
            descriptionKey: 'coupon_form.discount_card_desc_fixed_amount',
            value: CampaignCardValues.FixedAmount,
            img: '/app_assets/apps/amount-off.svg',
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            badges: ['Discount'],
            title: 'Coupon Code',
            titleKey: 'coupon_form.label_coupon_code',
            description: 'Use your existing code from other platforms.',
            descriptionKey: 'coupon_form.existing_code_hint',
            value: CampaignCardValues.ManualMaster,
            img: '/app_assets/apps/percentage-off.svg',
            shopTypes: [UserShopType.CustomShop],
          },
        ]
      },
      {
        img: '/app_assets/apps/recommendations.svg',
        badges: ['Recommendations'],
        title: 'Product Recommendations',
        titleKey: 'apps_page.card_titles.product_recommendations',
        description: 'This can meet a visitor based on where they currently are in their buying cycle, and guide them to the right product at the right time.',
        descriptionKey: 'apps_page.modal.recommendations_card_desc',
        value: CampaignCardValues.RecommendationsCard,
        setup_mins: 5,
        keywords: ['increase customers', 'increase revenue'],
        shopTypes: [UserShopType.ShopifyShop],
        children_open: false,
        children: [
          {
            badges: ['Automation'],
            title: 'Email Automation | Upsell',
            titleKey: 'apps_page.modal.rec_email_upsell_title',
            description: 'Recommend a product to the customer based on what they purchased.',
            descriptionKey: 'apps_page.modal.rec_email_upsell_desc',
            value: CampaignCardValues.UpsellEmails,
            img: '/app_assets/themes/emails/presets/upsell.svg',
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            badges: ['Automations'],
            title: 'SMS Automation | Upsell',
            titleKey: 'apps_page.modal.rec_sms_upsell_title',
            description: 'Recommend products based on recent purchases.',
            descriptionKey: 'apps_page.modal.rec_sms_upsell_desc',
            value: CampaignCardValues.SMSUpsell,
            tcpa_required: true,
            img: '/app_assets/themes/sms/presets/upsell.svg',
            shopTypes: [UserShopType.ShopifyShop],
          },
        ]
      },
      {
        img: '/app_assets/apps/birthday.svg',
        badges: ['Birthday'],
        title: 'Birthday',
        titleKey: 'apps_page.card_titles.birthday',
        description: 'If you do it right, your birthday emails can be another great part of your customer\'s amazing day.',
        descriptionKey: 'apps_page.modal.birthday_card_desc',
        value: CampaignCardValues.BirthdayCard,
        setup_mins: 5,
        keywords: ['increase customers', 'increase revenue'],
        shopTypes: [],
        children_open: false,
        children: [
          {
            badges: ['Automation'],
            title: 'Email Automation | Birthday',
            titleKey: 'apps_page.modal.birthday_email_auto_title',
            description: 'Build customer loyalty with Happy Birthday gift or discount offer.',
            descriptionKey: 'apps_page.modal.birthday_email_auto_desc',
            value: CampaignCardValues.BirthdayEmails,
            img: '/app_assets/themes/emails/presets/birthday.svg',
            shopTypes: [],
          },
          {
            badges: ['Automations'],
            title: 'SMS Automation | Birthday',
            titleKey: 'apps_page.modal.birthday_sms_auto_title',
            description: 'Build customer loyalty with Happy Birthday wishes, gifts or discount offers.',
            descriptionKey: 'apps_page.modal.birthday_sms_auto_desc',
            value: CampaignCardValues.SMSBirthday,
            tcpa_required: true,
            img: '/app_assets/themes/sms/presets/birthday.svg',
            shopTypes: [UserShopType.ShopifyShop],
          },
        ]
      },
      {
        img: '/app_assets/apps/winback.svg',
        badges: ['Winback', 'Loyalty'],
        title: 'Winback / Loyalty',
        titleKey: 'apps_page.card_titles.winback_loyalty',
        description: 'It can help any marketer regain potential customers and ensure their email list isn\'t being filled up by subscribers who aren\'t interested in the content.',
        descriptionKey: 'apps_page.modal.winback_card_desc',
        value: CampaignCardValues.WinbackCard,
        setup_mins: 2,
        keywords: ['increase customers', 'increase revenue'],
        shopTypes: [UserShopType.ShopifyShop],
        children_open: false,
        children: [
          {
            badges: ['Automation'],
            title: 'Email Automation | Winback',
            titleKey: 'apps_page.modal.winback_email_auto_title',
            description: 'Target customers who have not purchased anything in the specified period.',
            descriptionKey: 'apps_page.modal.winback_email_auto_desc',
            value: CampaignCardValues.WinbackEmails,
            img: '/app_assets/themes/emails/presets/winback.svg',
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            badges: ['Automations'],
            title: 'SMS Automation | Win-back',
            titleKey: 'apps_page.modal.winback_sms_auto_title',
            description: 'Win-back customers who have not purchased anything in the specified period',
            descriptionKey: 'apps_page.modal.winback_sms_auto_desc',
            value: CampaignCardValues.SMSWinback,
            tcpa_required: true,
            img: '/app_assets/themes/sms/presets/winback.svg',
            shopTypes: [UserShopType.ShopifyShop],
          },
        ]
      },
      // TODO update after implementing integrations redirects
      // {
      //   img: '/app_assets/apps/sync.svg',
      //   badges: ['Subscriber'],
      //   title: 'Subscriber Sync',
      //   description: 'If you do it right, your marketing emails can be another great part of your customer\'s amazing day.',
      //   value: CampaignCardValues.WinbackCard,
      //   setup_mins: 2,
      //   keywords: ['increase customers', 'increase subscribers'],
      //   children_open: false,
      //   children: [
      //     {
      //       badges: ['Automation'],
      //       title: 'Automation | Winback Email',
      //       description: 'Target customers who have not purchased anything in the specified period.',
      //       value: CampaignCardValues.WinbackEmails,
      //     },
      //   ]
      // },
      {
        img: '/app_assets/apps/gift-cards.svg',
        badges: ['Gift Cards', 'Unique Codes'],
        title: 'Gift Card Codes',
        titleKey: 'apps_page.card_titles.gift_cards',
        description: 'Offer e-gift cards to boost conversion rates and customer loyalty.',
        descriptionKey: 'apps_page.modal.gift_cards_desc',
        value: CampaignCardValues.GiftCards,
        setup_mins: 1,
        keywords: ['gift cards', 'unique codes', 'giftcards', 'rewards'],
        shopTypes: [UserShopType.ShopifyShop],
        children_open: false,
        children: [
          {
            badges: ['Discount'],
            title: 'Gift Card Onboarding',
            titleKey: 'apps_page.modal.gift_onboarding_title',
            description: 'Educational content about the gift cards and compliance.',
            descriptionKey: 'apps_page.modal.gift_onboarding_desc',
            value: CampaignCardValues.GiftCardsOnBoarding,
            button_text: 'Start',
            button_text_key: 'apps_page.modal.start',
            locked_text: 'Request Access',
            locked_text_key: 'apps_page.modal.request_access',
            img: '/app_assets/apps/gift-card-onboarding.svg',
            shopTypes: [UserShopType.ShopifyShop],
          },
          {
            badges: ['Discount'],
            title: 'Fixed Cash Amount',
            titleKey: 'apps_page.modal.gift_fixed_amount_title',
            description: 'e-Gift card with preloaded cash value',
            descriptionKey: 'apps_page.modal.gift_fixed_amount_desc',
            value: CampaignCardValues.GiftCardsUnique,
            locked_text: 'Request Access',
            locked_text_key: 'apps_page.modal.request_access',
            img: '/app_assets/apps/gift-card-fixed.svg',
            shopTypes: [UserShopType.ShopifyShop],
          }
        ]
      },
    ]
  }

  get isShopifyShop(): boolean {
    return _.get(this.userService.userInfo, 'shop.type') === UserShopType.ShopifyShop
  }

  get isOtherShop(): boolean {
    return !!_.get(this.userService.userInfo, 'shop.type')
  }

  get userPremiumEligible(): boolean {
    const isShopifyBudding = this.isShopifyShop && _.get(this.userService.userInfo, 'subscription.plan.payment_gateway_plan_identifier') === 'budding'
    // TODO: check this logic again if [Access to All Apps] is not suitable for free plan users
    const isShopifyNewPremiumUser = this.isShopifyShop && (this.userService.isNewPlanUser || this.userService.isNewPremiumPlanUser)
    return _.get(this.userService.userInfo, 'subscription.premium_themes', false) || isShopifyBudding || isShopifyNewPremiumUser
  }

  get userSMSEligible(): boolean {
    const userInfo = this.userService.userInfo
    return this.smsService.userIsEligible(userInfo)
  }

  get userOverCampaignLimit(): boolean {
    const userInfo = this.userService.userInfo
    const userSubscription = userInfo.subscription
    const isShopifyBudding = _.get(userInfo, 'shop.type') === 'ShopifyShop' && _.get(userInfo, 'subscription.plan.payment_gateway_plan_identifier') === 'budding'
    if (userSubscription && userSubscription.campaigns_limit) {
      return !isShopifyBudding && (userSubscription.campaigns_limit.active > userSubscription.campaigns_limit.limit)
    }
    return false
  }

  getPreviewOrder() {
    return this.apiCampaignService.getShopOrders(5).pipe(
      map(res => {
        if (res.total_count >= 5) {
          return _.sample(res.orders)
        } else {
          return null
        }
      })
    )
  }

  getRedirectUrl(value: CampaignCardValues) {
    switch (value) {
      case CampaignCardValues.BroadcastEmails:
      case CampaignCardValues.BroadcastCoupon:
      case CampaignCardValues.BroadcastNewsletter:
        return `/email-editor/broadcast`
      case CampaignCardValues.BirthdayEmails:
        return `/email-editor/birthday`
      case CampaignCardValues.UpsellEmails:
        return '/email-automation-builder/upsell/theme'
      case CampaignCardValues.WinbackEmails:
        return '/email-automation-builder/winback/theme'
      case CampaignCardValues.CartRecoveryEmails:
        return '/email-automation-builder/cart-recovery/theme'
      case CampaignCardValues.WelcomeNewAccountsCard:
        return `/email-automation-builder/welcome-email/accounts`
      case CampaignCardValues.WelcomeNewCustomerCard:
        return `/email-automation-builder/welcome-email/customers`
      case CampaignCardValues.WelcomeNewSubscriberCard:
        return `/email-automation-builder/welcome-email/subscribers`
      case CampaignCardValues.ThankYou:
        return `/email-automation-builder/welcome-email/thank-you`
      case CampaignCardValues.SMSBroadcastNoCoupon:
        return `/${RouteHeaderUrl.messaging}/${RouteHeaderUrl.sms}/broadcasts/new?type=broadcast`
      case CampaignCardValues.SMSBroadcastCoupon:
        return `/${RouteHeaderUrl.messaging}/${RouteHeaderUrl.sms}/broadcasts/new?type=coupon`
      case CampaignCardValues.SMSUpsell:
      case CampaignCardValues.SMSWinback:
      case CampaignCardValues.SMSBirthday:
      case CampaignCardValues.SMSCartRecovery:
        return `/${RouteHeaderUrl.messaging}/${RouteHeaderUrl.sms}/automations/new?type=${this.smsAutomationForCard[value]}`
      case CampaignCardValues.FixedAmount:
      case CampaignCardValues.Percentage:
      case CampaignCardValues.FreeShipping:
      case CampaignCardValues.BuyXGetY:
        return `/${RouteHeaderUrl.rewards}/${RouteHeaderUrl.coupons}/new?type=${this.discountTypeForCard[value]}`
      case CampaignCardValues.GenericCode:
      case CampaignCardValues.ManualMaster:
        return `/${RouteHeaderUrl.rewards}/${RouteHeaderUrl.coupons}/new?type=${value}`
      case CampaignCardValues.GiftCardsUnique:
        return `/${RouteHeaderUrl.rewards}/${RouteHeaderUrl.gift_cards}/new`
    }
  }

  getSkipCampaignObservable(isGettingStarted: boolean = false) {
    if (isGettingStarted) {
      return this.gettingStartedService.completeStatusAndSkipFirstCampaign()
    } else {
      return of({})
    }
  }

  getCreateCampaignObservable(campaignName: string = '', cardValue: CampaignCardValues, isGettingStarted: boolean = false, launchCampaign: boolean = false) {
    let campaign: Campaign
    let redirectUrl = ''
    return this.campaignsService.createCampaign({ name: campaignName || 'My campaign' } as Campaign).pipe(
      tap((next: Campaign) => {
        campaign = next
        if (isGettingStarted) {
          this.store.dispatch(new SetGettingStartedCampaign(campaign))
          this.gettingStartedService.completeStatusGroups({
            [GettingStartedGroupName.Campaign]: true,
          })
        }
      }),
      switchMap(() => isGettingStarted ? this.gettingStartedService.linkCampaign(campaign) : of({})),
      switchMap(() => this._createPluginIfNeeded(campaign, cardValue, isGettingStarted)),
      switchMap((url) => {
        redirectUrl = launchCampaign ? `/${RouteHeaderUrl.popups}/${RouteHeaderUrl.campaigns}/` : url
        if (isGettingStarted) {
          return launchCampaign ? this.gettingStartedService.completeStatusAndFirstCampaign(campaign.id) : this.gettingStartedService.completeStatus(true)
        }
        return of({})
      }),
      switchMap(() => launchCampaign ? this.campaignsService.setCampaignActive(campaign.id, CampaignState.On) : of({})),
      switchMap(() => of(redirectUrl))
      )
  }

  private _createPluginIfNeeded(campaign: Campaign, cardValue: CampaignCardValues, isGettingStarted: boolean = false) {
    const pluginType = this.pluginForCard[cardValue]
    let redirectUrl = ''
    if (pluginType) {
      redirectUrl = this.campaignsService.getConfigureLink(campaign.id, pluginType)

      const config = _.get(campaign[pluginType].config, 'v1', campaign[pluginType].config)
      const capture_configs = pluginType === CampaignPluginName.RecentActivity && campaign[pluginType] ? campaign[pluginType].capture_configs : undefined

      if (cardValue === CampaignCardValues.CollectPhoneNumbers) {
        redirectUrl += '/fields'
        delete config.form_config.email
        config.form_config.phone = {
          order: 1,
          country: {
            placeholder: {
              text: 'Country',
              enabled: true
            },
            default: 'US',
            flag_enabled: false
          },
          input_placeholder: {
            text: 'Phone',
            enabled: true
          },
          errors: {
            blank: 'Required',
            invalid: 'Phone is invalid',
            duplicated: 'Phone already used'
          },
          tcpa: {
            enabled: true,
            mob_quick_subscribe: false,
            mob_quick_subscribe_text: ''
          },
          duplicate_submissions: {
            allow_duplicating: false
          }
        }
      } else if (cardValue === CampaignCardValues.CampaignEmails) {
        redirectUrl += '/marketing_emails'
      }

      const data = { config, active: true, capture_configs_attributes: capture_configs }
      return this.apiCampaignService.putPlugin(campaign.id, pluginType, data).pipe(
        switchMap(() => of(redirectUrl))
      )
    } else {
      if (cardValue === CampaignCardValues.NeedsUpgrade) {
        redirectUrl = '/choose-plan'
      } else {
        redirectUrl = `/${RouteHeaderUrl.popups}/${RouteHeaderUrl.campaigns}/${campaign.id}/plugin`
      }
      return of(redirectUrl)
    }
  }
}
